import { useSourceSystem } from '@front/hook/Api/SourceSystem';
import { AuthDomain } from 'auth-lib-front';
import { useEffect, useMemo } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { DomainV2 } from 'troppus-common/domain';
import type { FormValues } from '../types';

import Project = AuthDomain.Object.Project;
import SourceSystem = DomainV2.Import.SourceSystem;

type Params = {
  projectId: Project.Id;
  initialSourceSystemId: string | undefined;
  sourceSystemId: string | undefined;
  setValue: UseFormSetValue<FormValues>;
};

export function useSourceSystemSelection({ projectId, initialSourceSystemId, sourceSystemId, setValue }: Params) {
  const { data: sourceSystems, isPending: sourcesPending } = useSourceSystem.List(projectId);

  useEffect(() => {
    if (sourceSystemId) return;
    if (initialSourceSystemId) {
      const exists = sourceSystems?.some((s) => s.id === initialSourceSystemId);
      if (exists) {
        setValue('sourceSystemId', initialSourceSystemId);
        return;
      }
    }
    const first = sourceSystems?.[0];
    if (first) setValue('sourceSystemId', first.id);
  }, [initialSourceSystemId, sourceSystems, sourceSystemId, setValue]);

  const sourceSystemSelectOptions = useMemo(
    () =>
      (sourceSystems ?? []).map((s: SourceSystem) => ({
        value: s.id,
        label: s.displayName,
      })),
    [sourceSystems],
  );

  const selectedSourceSystemName = sourceSystems?.find((s) => s.id === sourceSystemId)?.displayName ?? '';

  return {
    sourcesPending,
    sourceSystemSelectOptions,
    selectedSourceSystemName,
  };
}
